import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import { useId, useState } from 'react';
import { FormDialog } from '~/components/dialogs/FormDialog';
import { ClapperboardIcon } from './ClapperboardIcon';
import { isDirectMediaUrl, TUTORIAL_VIDEOS, type TutorialVideoId } from './tutorialVideos';

const TAGS = CT.tutorialVideo;

export const VIDEO_UNAVAILABLE_TOOLTIP = 'Video not available yet';

/**
 * Where the tooltip sits under the icon. It always opens downward (the buttons live at the top of
 * a card or bar, and an upward tooltip would run off it); this picks the horizontal anchor so it
 * stays inside the page: `end` for an icon near the right edge, `start` for one near the left, and
 * `start-then-end` for a card header that stacks left-aligned on a phone and right-aligns from `sm`.
 */
type TooltipAlign = 'start' | 'center' | 'end' | 'start-then-end';

const TOOLTIP_ALIGN_CLASS: Record<TooltipAlign, string> = {
  start: 'left-0',
  center: 'left-1/2 -translate-x-1/2',
  end: 'right-0',
  'start-then-end': 'left-0 sm:left-auto sm:right-0',
};

interface Props {
  videoId: TutorialVideoId;
  /** Positioning for the wrapper around the button and its tooltip (spacing, `absolute`, ...). */
  className?: string;
  /** Size of the clapperboard; the default 24px fills the 32px button together with its padding. */
  iconClassName?: string;
  tooltipAlign?: TooltipAlign;
}

/**
 * [MCV-1945] A clapperboard button that opens a tutorial video. While the video has no URL it only
 * explains itself: hovering or focusing shows "Video not available yet" and clicking does nothing.
 * The button stays focusable and `aria-disabled` (not `disabled`) so keyboard and screen reader
 * users get the same explanation as a mouse user.
 *
 * The tooltip is plain CSS (`group-hover` / `group-has-[:focus-visible]`) and always in the DOM, so
 * `aria-describedby` can point at it. It is not Tooltip.tsx, which is an info-icon Popover trigger.
 */
export const TutorialVideoButton = ({
  videoId,
  className,
  iconClassName = 'h-6 w-6',
  tooltipAlign = 'center',
}: Props) => {
  const { title, url } = TUTORIAL_VIDEOS[videoId];
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const available = url !== null;

  return (
    <span className={classNames('group inline-flex', className)}>
      {/* Its own box, so `className` can position the wrapper (`absolute`) without fighting this
          `relative`: the tooltip anchors to the button, wherever the wrapper ends up. */}
      <span className="relative inline-flex">
        <button
          type="button"
          data-cy={`${TAGS.button}-${videoId}`}
          aria-label={available ? title : `${title}, video not available yet`}
          aria-describedby={tooltipId}
          aria-disabled={available ? undefined : true}
          onClick={available ? () => setOpen(true) : undefined}
          className={classNames(
            'block cursor-pointer rounded-control p-1 text-grey-400 transition-colors',
            'hover:bg-grey-900/50 hover:text-grey-200',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-50'
          )}
        >
          <ClapperboardIcon className={iconClassName} />
        </button>
        {/* `pt-1.5` rather than a margin: the pointer can travel from the icon onto the tooltip
          without leaving the group. */}
        <span
          id={tooltipId}
          role="tooltip"
          data-cy={TAGS.tooltip}
          className={classNames(
            'invisible absolute top-full z-50 w-max max-w-[14rem] pt-1.5 opacity-0 transition-opacity',
            'group-hover:visible group-hover:opacity-100',
            'group-has-[:focus-visible]:visible group-has-[:focus-visible]:opacity-100',
            TOOLTIP_ALIGN_CLASS[tooltipAlign]
          )}
        >
          <span className="block rounded-lg border border-grey-900 bg-white px-2.5 py-1.5 text-left text-xs font-normal leading-snug text-grey-200 shadow-xl">
            {available ? title : VIDEO_UNAVAILABLE_TOOLTIP}
          </span>
        </span>
      </span>
      {available && (
        <FormDialog
          editing={open}
          onClose={() => setOpen(false)}
          title={title}
          width="3xl"
          zIndex="top"
        >
          <TutorialVideoPlayer title={title} url={url} />
        </FormDialog>
      )}
    </span>
  );
};

const TutorialVideoPlayer = ({ title, url }: { title: string; url: string }) =>
  isDirectMediaUrl(url) ? (
    <video
      controls
      src={url}
      data-cy={TAGS.video}
      aria-label={title}
      className="max-h-[70vh] w-full rounded-lg bg-black"
    />
  ) : (
    <iframe
      src={url}
      title={title}
      data-cy={TAGS.frame}
      allow="fullscreen; picture-in-picture"
      allowFullScreen
      className="aspect-video w-full rounded-lg bg-black"
    />
  );
